import React from 'react';
import {Icons} from '../../assets';
import {Strings} from '../../constants';
import {Container, Spacer, SpacerSize, Stack, StackDirection, StackSpacing} from '../layout';
import {ContainerSize, ContainerVariant} from '../layout/container';
import {BackgroundPattern} from '../signup-features/background-pattern';
import {Heading, HeadingVariant, HtmlTag, MarginBottom, Text, TextColor, TextVariant} from '../typography';

export const OnboardingFeatures: React.FC<{className?: string}> = ({className = ''}) => {
  return (
    <div
      className={`relative flex flex-col justify-center items-center p-4 sm:p-6 md:p-8 lg:p-16 min-h-screen overflow-hidden ${className}`.trim()}
      style={{
        background: 'linear-gradient(180deg, #EDE9FB 0%, #F7E8F3 53%, #E5E7FB 100%)',
      }}>
      <BackgroundPattern />

      <Container
        variant={ContainerVariant.Centered}
        size={ContainerSize.MD}
        background="bg-transparent"
        className="relative z-10 w-full max-w-2xl">
        <div className="bg-white rounded-2xl p-4 sm:p-6 md:p-8 lg:p-10 shadow-lg w-full">
          <div className="inline-flex items-center gap-3 px-3 sm:px-4 py-2 bg-gray-100 rounded-full shadow-sm">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
              <Text
                variant={TextVariant.BODY_SMALL}
                color={TextColor.MUTED}
                className="uppercase tracking-wide text-xs sm:text-sm"
                as={HtmlTag.SPAN}>
                {Strings.ONBOARDING_LABEL_FIRST_ROBOTICS}
              </Text>
            </div>
            <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
            <Text
              variant={TextVariant.BODY_SMALL}
              color={TextColor.MUTED}
              className="uppercase tracking-wide text-xs sm:text-sm"
              as={HtmlTag.SPAN}>
              {Strings.ONBOARDING_LABEL_VISUAL_PORTFOLIOS}
            </Text>
            <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
            <Text
              variant={TextVariant.BODY_SMALL}
              color={TextColor.MUTED}
              className="uppercase tracking-wide text-xs sm:text-sm"
              as={HtmlTag.SPAN}>
              {Strings.ONBOARDING_LABEL_INTERNSHIPS}
            </Text>
          </div>

          <Spacer size={SpacerSize.LG} />

          <Heading level={1} variant={HeadingVariant.PAGE_TITLE} marginBottom={MarginBottom.NONE} className="text-gray-900 break-words">
            {Strings.ONBOARDING_HEADLINE_PART1}{' '}
            <span className="bg-gradient-to-r from-blue-800 to-blue-400 bg-clip-text text-transparent font-bold">
              {Strings.ONBOARDING_HEADLINE_ROBOTICS}
            </span>{' '}
            <span className="bg-gradient-to-r from-cyan-600 to-lime-400 bg-clip-text text-transparent font-bold">
              {Strings.ONBOARDING_HEADLINE_GRIND}
            </span>{' '}
            {Strings.ONBOARDING_HEADLINE_PART2}
          </Heading>

          <Spacer size={SpacerSize.LG} />

          <Text variant={TextVariant.BODY} color={TextColor.SECONDARY}>
            {Strings.ONBOARDING_PARAGRAPH_ONE}
          </Text>

          <Spacer size={SpacerSize.LG} />

          <Text variant={TextVariant.BODY} color={TextColor.SECONDARY}>
            {Strings.ONBOARDING_PARAGRAPH_TWO}
          </Text>

          <Spacer size={SpacerSize.LG} />

          <Stack direction={StackDirection.Vertical} spacing={StackSpacing.MD}>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                <Text variant={TextVariant.BODY_SMALL} className="font-bold text-blue-500 text-sm" as={HtmlTag.SPAN}>
                  {Strings.ONBOARDING_CREDS_LABEL.charAt(0)}
                </Text>
              </div>
              <Text variant={TextVariant.BODY} color={TextColor.SECONDARY} className="flex-1">
                <strong>{Strings.ONBOARDING_CREDS_LABEL}</strong>: {Strings.ONBOARDING_CREDS_DESCRIPTION}
              </Text>
            </div>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                <Text variant={TextVariant.BODY_SMALL} className="font-bold text-blue-500 text-sm" as={HtmlTag.SPAN}>
                  {Strings.ONBOARDING_RECCOS_LABEL.charAt(0)}
                </Text>
              </div>
              <Text variant={TextVariant.BODY} color={TextColor.SECONDARY} className="flex-1">
                <strong>{Strings.ONBOARDING_RECCOS_LABEL}</strong>: {Strings.ONBOARDING_RECCOS_DESCRIPTION}
              </Text>
            </div>
          </Stack>

          <Spacer size={SpacerSize.LG} />

          <Text variant={TextVariant.BODY_SMALL} color={TextColor.MUTED} className="uppercase tracking-wide">
            {Strings.ONBOARDING_SECTION_WHY_IT_MATTERS}
          </Text>

          <Spacer size={SpacerSize.MD} />

          <Stack direction={StackDirection.Vertical} spacing={StackSpacing.MD}>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center">
                <img alt="Check" className="w-3 h-3" src={Icons.ONBOARDING_CHECK} />
              </div>
              <Text variant={TextVariant.BODY} color={TextColor.SECONDARY} className="flex-1">
                {Strings.ONBOARDING_CHECKPOINT_ONE}
              </Text>
            </div>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center">
                <img alt="Check" className="w-3 h-3" src={Icons.ONBOARDING_CHECK} />
              </div>
              <Text variant={TextVariant.BODY} color={TextColor.SECONDARY} className="flex-1">
                {Strings.ONBOARDING_CHECKPOINT_TWO}
              </Text>
            </div>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center">
                <img alt="Check" className="w-3 h-3" src={Icons.ONBOARDING_CHECK} />
              </div>
              <Text variant={TextVariant.BODY} color={TextColor.SECONDARY} className="flex-1">
                {Strings.ONBOARDING_CHECKPOINT_THREE}
              </Text>
            </div>
          </Stack>
          <Spacer size={SpacerSize.LG} />

          <Text variant={TextVariant.BODY} color={TextColor.SECONDARY} className="font-medium">
            {Strings.ONBOARDING_FINAL_SENTENCE}
          </Text>
        </div>
      </Container>
    </div>
  );
};
